/**
 * Voice session context — lifts `useGeminiLive` above layout-dependent UI.
 *
 * VoiceWidget is portaled and repositioned when panels open, auto-organize
 * runs, or voice dock state flips. Mounting the Gemini Live client inside
 * VoiceWidget tied session lifetime to that UI subtree — any remount called
 * `stop()` and killed the mic/WebSocket mid-call. The provider mounts once
 * at CanvasShell root so transport survives all layout changes.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  useGeminiLive,
  type UseGeminiLiveResult,
} from './voice/useGeminiLive';
import { useCanvasConfig } from './CanvasContext';

const VoiceSessionContext = createContext<UseGeminiLiveResult | null>(null);

export function VoiceSessionProvider({ children }: { children: ReactNode }) {
  const canvasConfig = useCanvasConfig();
  const { persona } = canvasConfig;

  const liveOptions = useMemo(
    () => ({
      persona: { ...persona, tenant: canvasConfig.tenant },
      mockScenario: persona.mockScenario,
      tokenEndpoint: persona.tokenEndpoint,
    }),
    [persona, canvasConfig.tenant, persona.mockScenario, persona.tokenEndpoint],
  );

  const live = useGeminiLive(liveOptions);

  return (
    <VoiceSessionContext.Provider value={live}>{children}</VoiceSessionContext.Provider>
  );
}

export function useVoiceSession(): UseGeminiLiveResult {
  const ctx = useContext(VoiceSessionContext);
  if (!ctx) {
    throw new Error('useVoiceSession must be used within VoiceSessionProvider');
  }
  return ctx;
}
